import { StudentType } from '@wonderschool/common-base-types';
import find from 'lodash/find';
import { organizationStudentsOnListen, parentStudentsOnListen, saveStudent } from '../../students/studentsAPI';
import { getUsersRoomsOrLocations } from '../helpers/getUsersRoomsOrLocations';
import store from '../store';
import {
  STUDENTS_FETCHED,
  STUDENTS_FETCH_FAILED,
  STUDENTS_LISTENER_STARTED,
  STUDENT_ADDED,
  STUDENT_ADD_CLEARED,
  STUDENT_SELECTED,
  STUDENT_SELECTION_CLEARED,
  STUDENT_UPDATED,
  USER_EMAIL_RESET_LINK_SENT,
} from './types';

const identifyStudentLocation = (studentData) => {
  let location = '';
  if (!studentData?.rooms?.length) return location;
  const room = studentData.rooms[0];
  if (!room?.length) return location;

  const roomStore = store.getState()?.rooms || [];
  if (roomStore?.list?.length) {
    // Extract current location from room store
    const roomObject = find(roomStore.list, (r) => r.id === room);
    if (roomObject?.location?.length) location = roomObject.location;
  }

  return location;
};

export const studentsListenerStarted = () => ({
  type: STUDENTS_LISTENER_STARTED,
});

export const studentsFetched = (students) => ({
  type: STUDENTS_FETCHED,
  students,
});

export const studentsFetchFailed = (error) => ({
  type: STUDENTS_FETCH_FAILED,
  error,
});

export const studentAdded = (student) => ({
  type: STUDENT_ADDED,
  student,
});

export const studentAddCleared = () => ({
  type: STUDENT_ADD_CLEARED,
});

export const studentUpdated = () => ({
  type: STUDENT_UPDATED,
});

export const studentSelected = (student) => ({
  type: STUDENT_SELECTED,
  student,
});

export const studentSelectionCleared = () => ({
  type: STUDENT_SELECTION_CLEARED,
});

export const emailResetLinkSent = () => ({
  type: USER_EMAIL_RESET_LINK_SENT,
});

export const startOrganizationStudentsListener = (organizationId, parentUserId) => (dispatch) => {
  dispatch(studentsListenerStarted());

  // parents must ONLY query for students they are associated with
  if (parentUserId) {
    return parentStudentsOnListen(
      organizationId,
      parentUserId,
      (data) => {
        if (data) dispatch(studentsFetched(data));
      },
      (error) => {
        console.log(error.message);
        dispatch(studentsFetchFailed(error.message));
      }
    );
  }

  const validRoomIds = getUsersRoomsOrLocations('rooms', organizationId);
  if (validRoomIds === null) {
    return () => {};
  }

  return organizationStudentsOnListen(
    { organizationId, validRoomIds },
    (data) => {
      if (data) dispatch(studentsFetched(data));
    },
    (error) => {
      console.log(error.message);
      dispatch(studentsFetchFailed(error.message));
    }
  );
};

export const organizationAddStudent =
  (organizationId, { combinedFamily, ...studentData }) =>
  (dispatch) => {
    const location = identifyStudentLocation(studentData);
    return saveStudent(organizationId, {
      ...studentData,
      location,
    }).then((student) => dispatch(studentAdded(student)));
  };

export const organizationUpdateStudent =
  (
    organizationId,
    // combinedFamily is used on the front end only and should not be stored in the db
    { combinedFamily, ...studentData }
  ) =>
  async (dispatch) => {
    const location = identifyStudentLocation(studentData);
    await saveStudent(organizationId, {
      ...studentData,
      location,
    });
    return dispatch(studentUpdated());
  };

export type StudentsReducerState = {
  selectedStudent: StudentType | null;
  count: number;
  list: StudentType[];
  isLoading: boolean;
  error: string | null;
};
const initialState: StudentsReducerState = {
  selectedStudent: null,
  count: 0,
  list: [],
  isLoading: true,
  error: null,
};

export const studentsReducer = (state = initialState, action) => {
  switch (action.type) {
    case STUDENTS_LISTENER_STARTED:
      return { ...state, isLoading: true, error: null };

    case STUDENTS_FETCHED: {
      let selectedStudent = state?.selectedStudent;

      if (selectedStudent) {
        selectedStudent = action.students.list.find((student) => student.id === selectedStudent?.id);
      }

      return {
        ...state,
        count: action.students.count,
        list: action.students.list,
        selectedStudent,
        isLoading: false,
        error: null,
      };
    }

    case STUDENTS_FETCH_FAILED:
      return { ...state, isLoading: false, error: action.error };

    case STUDENT_SELECTED:
      return { ...state, selectedStudent: action.student };

    // Replace selected with the newly updated data.
    // New data is fetched by the listener as soon as listener receives the update.
    case STUDENT_UPDATED: {
      return {
        ...state,
      };
    }

    case STUDENT_ADDED: {
      return {
        ...state,
        addedStudent: action.student,
      };
    }

    case STUDENT_ADD_CLEARED: {
      return {
        ...state,
        addedStudent: {},
      };
    }
    case STUDENT_SELECTION_CLEARED:
      return { ...state, selectedStudent: {} };

    default:
      return state;
  }
};
